import { DEFAULT_ENDPOINT, debug, warn } from "../core/config.js";
import { currentToken, findCompletion, searchPageUrl } from "./match.js";
import { createOverlay, type GhostOverlay } from "./overlay.js";
import { loadWordlist } from "./wordlist.js";

/**
 * Typeahead — inline word completion on `input[data-slater-typeahead]`:
 * generic English words (a frequency-ranked list fetched on first focus), not
 * site content, so no per-keystroke server calls and nothing lands in search
 * analytics. Tab / ArrowRight-at-end / tapping the ghost accepts; Enter
 * searches what was actually typed (never silently accepts the ghost).
 *
 * The feature is purely input-local, which is what lets it live in two
 * places: standalone inputs (e.g. a navbar box outside any block — Enter then
 * navigates to `data-slater-search-page` with the `?query=` deep link the
 * search block already reads), and in-block `[data-slater-search]` inputs,
 * where accepting dispatches a bubbling `input` event and the existing
 * debounced binder takes it from there — zero coupling to SlaterList.
 */

/** Words are one shared list; the per-input closure only tracks UI state. */
let words: readonly string[] | null = null;
const allOverlays: GhostOverlay[] = [];

export function initTypeahead(): void {
  const els = document.querySelectorAll("[data-slater-typeahead]");
  let bound = 0;
  for (const el of els) {
    if (!(el instanceof HTMLInputElement)) {
      warn("typeahead: data-slater-typeahead must be on an <input>.", el);
      continue;
    }
    bindInput(el);
    bound += 1;
  }
  if (bound === 0) return;
  debug(`typeahead: ${bound} input(s) bound`);

  // boot()'s bfcache rehydrate only runs on pages with blocks; a standalone
  // navbar input restored from the back/forward cache would otherwise keep a
  // stale ghost floating over the page.
  window.addEventListener("pageshow", (event) => {
    if ((event as PageTransitionEvent).persisted) {
      for (const overlay of allOverlays) overlay.hide();
    }
  });
}

function bindInput(input: HTMLInputElement): void {
  const endpoint = (window.SlaterSearchConfig?.endpoint ?? DEFAULT_ENDPOINT).replace(/\/+$/, "");

  // Browser autofill/autocorrect UIs fight the ghost visually; suppress them
  // unless the author already made an explicit choice.
  if (!input.hasAttribute("autocomplete")) input.setAttribute("autocomplete", "off");
  if (!input.hasAttribute("autocorrect")) input.setAttribute("autocorrect", "off");
  if (!input.hasAttribute("autocapitalize")) input.setAttribute("autocapitalize", "none");
  if (!input.hasAttribute("spellcheck")) input.setAttribute("spellcheck", "false");
  if (!input.hasAttribute("aria-autocomplete")) input.setAttribute("aria-autocomplete", "inline");

  const state = {
    /** The remainder currently ghosted, if any. */
    suggestion: null as string | null,
    /** IME composition in progress — suggesting mid-composition garbles it. */
    composing: false,
    /** Escape pressed; stays set until the next value change. */
    dismissed: false,
    /** Our own accept dispatches `input`; don't immediately re-suggest on it. */
    suppressNextInput: false,
    lastValueLength: input.value.length,
  };

  const overlay = createOverlay(input, accept);
  allOverlays.push(overlay);

  function hideSuggestion(): void {
    state.suggestion = null;
    overlay.hide();
  }

  function update(): void {
    if (!words || words.length === 0 || state.composing || state.dismissed) {
      hideSuggestion();
      return;
    }
    const token = currentToken(input.value, input.selectionStart, input.selectionEnd);
    const remainder = token
      ? findCompletion(words, token, input.value.length, input.maxLength)
      : null;
    if (!remainder) {
      hideSuggestion();
      return;
    }
    state.suggestion = remainder;
    overlay.show(input.value, remainder);
  }

  function accept(): void {
    const remainder = state.suggestion;
    if (!remainder) return;
    input.value += remainder;
    input.setSelectionRange(input.value.length, input.value.length);
    hideSuggestion();
    state.lastValueLength = input.value.length;
    state.suppressNextInput = true;
    // The one bridge to the rest of the embed: in-block binders listen for
    // exactly this event, so an accepted word searches like a typed one.
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }

  function ensureWordlist(): void {
    if (words !== null) return;
    void loadWordlist(endpoint).then((list) => {
      words = list;
      // The visitor may have typed while the list was in flight.
      if (document.activeElement === input) update();
    });
  }

  input.addEventListener("focus", ensureWordlist);
  // An autofocused (or just quickly-focused) input got its focus event before
  // the embed booted; the event is gone but the intent isn't.
  if (document.activeElement === input) ensureWordlist();

  input.addEventListener("input", (event) => {
    if (state.suppressNextInput) {
      state.suppressNextInput = false;
      state.lastValueLength = input.value.length;
      return;
    }
    // inputType is the reliable signal; the length check is the fallback for
    // synthetic events (tests) and older browsers that omit it.
    const deleting =
      Boolean((event as InputEvent).inputType?.startsWith("delete")) ||
      input.value.length < state.lastValueLength;
    state.lastValueLength = input.value.length;
    state.dismissed = false;
    if (deleting) {
      // Deleting means "that's not what I wanted" — re-offering the same
      // completion straight back would be obnoxious.
      hideSuggestion();
      return;
    }
    update();
  });

  input.addEventListener("keydown", (event) => {
    const key = event.key;
    if (key === "Tab" && overlay.visible && !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey) {
      event.preventDefault();
      accept();
      return;
    }
    if (key === "ArrowRight" && overlay.visible && caretAtEnd()) {
      event.preventDefault();
      accept();
      return;
    }
    if (key === "Escape") {
      if (overlay.visible) {
        state.dismissed = true;
        hideSuggestion();
      }
      return;
    }
    if (key === "Enter") {
      hideSuggestion();
      const page = input.getAttribute("data-slater-search-page");
      const q = input.value.trim();
      if (page && q) {
        // Standalone mode: stop the Webflow <form> from submitting and go to
        // the search page instead — initGlobalSearch() picks up ?query=.
        event.preventDefault();
        window.location.assign(searchPageUrl(page, q, window.location.origin));
      }
      return;
    }
    if (key === "ArrowLeft" || key === "ArrowUp" || key === "ArrowDown" || key === "Home" || key === "End") {
      // The caret is leaving the end of the value — the ghost no longer
      // continues what's under the caret.
      hideSuggestion();
    }
  });

  input.addEventListener("blur", hideSuggestion);
  // A click/tap on the input itself repositions the caret; the ghost span is
  // not part of this — it has its own pointerdown that accepts.
  input.addEventListener("pointerdown", hideSuggestion);

  input.addEventListener("compositionstart", () => {
    state.composing = true;
    hideSuggestion();
  });
  input.addEventListener("compositionend", () => {
    state.composing = false;
  });

  function caretAtEnd(): boolean {
    return (
      input.selectionStart === input.value.length && input.selectionEnd === input.value.length
    );
  }
}
